import type { WebSocketMessage } from "@/services/WebSocketManager";

export interface CaseCheckingWsDocument {
  _id?: string;
  id?: string;
  case_id?: string;
  company_id?: string;
  classification?: string | null;
  updated_at?: string | number;
  metadata?: {
    active_task_id?: string | null;
    [key: string]: unknown;
  } | null;
}

export interface CaseCheckingWsPayload {
  operation?: string;
  full_document?: CaseCheckingWsDocument;
  timestamp?: string | number;
  payload?: CaseCheckingWsPayload;
}

export function parseCaseCheckingWsMessage(
  raw: WebSocketMessage | CaseCheckingWsPayload | string | null | undefined,
): CaseCheckingWsPayload | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as CaseCheckingWsPayload;
      return parsed.payload ?? parsed;
    } catch {
      return null;
    }
  }
  if ("operation" in raw || "full_document" in raw) {
    const direct = raw as CaseCheckingWsPayload;
    return direct.payload ?? direct;
  }
  const envelope = raw.payload as CaseCheckingWsPayload | undefined;
  return envelope?.payload ?? envelope ?? null;
}

export function caseCheckingDocId(doc: CaseCheckingWsDocument): string | null {
  const id = doc._id ?? doc.id;
  return id != null ? String(id) : null;
}

export function caseCheckingCaseId(doc: CaseCheckingWsDocument): string | null {
  const id = doc.case_id ?? doc._id ?? doc.id;
  return id != null ? String(id) : null;
}

export function matchesCaseCheckingCompany(
  doc: CaseCheckingWsDocument,
  companyId: string | null | undefined,
): boolean {
  if (!companyId) return false;
  if (!doc.company_id) return true;
  return String(doc.company_id) === String(companyId);
}

/** Runner task id from a case websocket payload (`metadata.active_task_id`). */
export function caseCheckingActiveTaskId(doc: CaseCheckingWsDocument): string | null {
  const raw = doc.metadata?.active_task_id;
  if (raw == null) return null;
  const taskId = String(raw).trim();
  return taskId.length > 0 ? taskId : null;
}
